import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';
import useFeedbackStore from '@components/ui/general/feedback/FeedbackStore';
import { ChevronDown, ChevronUp, ArrowLeft, LogOut, Menu, PlayCircle, MessageSquareHeart, History, Settings, Heart } from 'lucide-react';
import classnames from 'classnames';
import DebugPanel from '@components/developer/ui/DebugPanel';
import { getAssetPath, getGameVersion } from '@utils/brandHelper';
import TutorialModal from '@components/ui/general/TutorialModal/TutorialModal';
import useConfigStore from '@/store/useConfigStore';
import { useT } from '@/i18n';

import './MainMenu.css';

const SHOW_TUTORIAL = import.meta.env.VITE_SHOW_TUTORIAL === 'true';

function MainMenu({ onOpenTutorial, onTutorialComplete, showTutorialHint = false, showTutorialModal: externalShowTutorial = false }) {
  // Obtenemos todo lo que necesitamos de nuestro hook central
  const { user, logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();

  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Internal tutorial modal state (for when opened from menu)
  const [internalShowTutorial, setInternalShowTutorial] = useState(false);

  // Combined: show tutorial if either internal or external says so
  const showTutorial = internalShowTutorial || externalShowTutorial;

  // Show back button only when not on dashboard
  const showBackButton = location.pathname !== '/' && location.pathname !== '/dashboard';

  const { isHistoryVisible, toggleHistory, messages } = useFeedbackStore();
  const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const feedbackClasses = classnames('feedback-text', latestMessage && `feedback-${latestMessage.type}`);

  const handleBackToDashboard = () => {
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleTutorialClick = () => {
    setIsDropdownOpen(false);
    if (onOpenTutorial) {
      onOpenTutorial();
    } else {
      // Open internal modal if no external handler
      setInternalShowTutorial(true);
    }
  };

  const handleCloseTutorial = () => {
    setInternalShowTutorial(false);
    // Also notify parent if it's controlling externally
    if (onTutorialComplete) {
      onTutorialComplete();
    }
  };

  const handleFeedbackClick = () => {
    setIsDropdownOpen(false);
    navigate('/feedback');
  };

  const handleLogClick = () => {
    setIsDropdownOpen(false);
    toggleHistory();
  };

  const handleCreditsClick = () => {
    setIsDropdownOpen(false);
    navigate('/credits');
  };

  const handleSettingsClick = () => {
    setIsDropdownOpen(false);
    // Open config modal - URL will be updated by useConfigModal hook in App.jsx
    useConfigStore.getState().openModal();
  };

  if (!user) return null;

  return (
    <nav className="main-menu">
      <div className="menu-logo-container">
        <img
          src={getAssetPath('assets/logosimple.webp')}
          alt="Logo"
          className="menu-logo"
          onClick={() => navigate('/dashboard')}
          style={{ cursor: 'pointer' }}
        />
        <DebugPanel />
      </div>
      <div className="feedback-display">
        {latestMessage ? (
          <span className={feedbackClasses} dangerouslySetInnerHTML={{ __html: latestMessage.message }} />
        ) : (
          <span>{user?.name ? t('menu.welcome', { name: user.name }) : t('menu.welcome_user')}</span>
        )}
      </div>

      <div className="menu-actions">
        {/* Dropdown Menu */}
        <div className="menu-dropdown" ref={dropdownRef}>
          <button
            className={classnames('button-square', 'menu-dropdown-trigger', { 'active': isDropdownOpen })}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            title={t('menu.menu')}
            data-sfx="button"
          >
            <Menu />
            {isDropdownOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {/* Tutorial Hint Tooltip */}
          {SHOW_TUTORIAL && showTutorialHint && !isDropdownOpen && (
            <div className="menu-tutorial-hint">
              {t('menu.watch_tutorial')}
              <span className="menu-tutorial-hint-arrow" />
            </div>
          )}

          {isDropdownOpen && (
            <div className="menu-dropdown-content">
              <button className="menu-dropdown-item" onClick={handleSettingsClick} data-sfx="button">
                <Settings size={18} />
                <span>{t('menu.settings')}</span>
              </button>
              {SHOW_TUTORIAL && (
                <button className="menu-dropdown-item" onClick={handleTutorialClick} data-sfx="button">
                  <PlayCircle size={18} />
                  <span>{t('menu.tutorial')}</span>
                </button>
              )}
              <button className="menu-dropdown-item" onClick={handleFeedbackClick} data-sfx="button">
                <MessageSquareHeart size={18} />
                <span>{t('menu.feedback')}</span>
              </button>
              <button className="menu-dropdown-item" onClick={handleLogClick} data-sfx="button">
                <History size={18} />
                <span>{isHistoryVisible ? t('menu.hide_log') : t('menu.show_log')}</span>
              </button>
              <button className="menu-dropdown-item" onClick={handleCreditsClick} data-sfx="button">
                <Heart size={18} />
                <span>{t('menu.credits')}</span>
              </button>
              <div className="menu-dropdown-version">{getGameVersion()}</div>
            </div>
          )}
        </div>

        {showBackButton && (
          <button onClick={handleBackToDashboard} className="button-square" data-sfx="button">
            <ArrowLeft />
          </button>
        )}

        <button onClick={logout} className="button-square" disabled={isLoggingOut} data-sfx="button">
          <LogOut />
          {isLoggingOut && <span className="sr-only">{t('menu.logging_out')}</span>}
        </button>
      </div>

      {/* Tutorial Modal */}
      {SHOW_TUTORIAL && (
        <TutorialModal
          isOpen={showTutorial}
          onClose={handleCloseTutorial}
          onComplete={handleCloseTutorial}
        />
      )}
    </nav>
  );
}

export default MainMenu;